/**
 * silkcity-a11y-fixes.js  (v3 — MutationObserver for dynamic content)
 * Accessibility fixes for silkcityfibers.com
 *
 * HOW TO INSTALL:
 *   Shopify Admin → Online Store → Themes → Edit Code
 *   → assets/ → open silkcity-a11y-fixes.js → replace all contents → Save
 *   In layout/theme.liquid just before </body>:
 *   <script src="{{ 'silkcity-a11y-fixes.js' | asset_url }}" defer></script>
 *
 * WHAT CHANGED IN V3:
 *   Fix 1 — role="none" was absent at DOMContentLoaded because Shopify
 *            injects section wrappers dynamically. Now uses a MutationObserver
 *            on <body> to catch the element whenever it appears.
 *   Fix 5 — Sort select uses a broader set of selectors and also retries
 *            after a short delay to catch late-rendered elements.
 */

(function () {
  'use strict';

  var LOG_PREFIX = '[a11y]';
  var log = {
    info: function (m) { console.info(LOG_PREFIX  + ' \u2139 ' + m); },
    ok:   function (m) { console.log(LOG_PREFIX   + ' \u2713 ' + m); },
    warn: function (m) { console.warn(LOG_PREFIX  + ' \u26A0 ' + m); },
    err:  function (m) { console.error(LOG_PREFIX + ' \u2717 ' + m); }
  };

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  function $$(sel, root) {
    try { return Array.from((root || document).querySelectorAll(sel)); }
    catch (e) { log.err('Bad selector: ' + sel); return []; }
  }

  /* =========================================================================
   * FIX 1 — Footer role="none" suppresses contentinfo landmark
   *
   * ROOT CAUSE (confirmed by v2 console output):
   *   Strategy 5 in v2 found ZERO elements with role="none" at DOMContentLoaded.
   *   This means Shopify is injecting the footer section wrapper AFTER the DOM
   *   is ready — either via a section group script or a deferred render.
   *
   * FIX:
   *   1. Run the remover once on DOMContentLoaded (catches static HTML).
   *   2. Set up a MutationObserver on <body> so any element added later that
   *      has role="none" wrapping a <footer> is patched immediately.
   *   3. Also run once more after 1s and 3s as belt-and-suspenders for any
   *      section that renders via a slow JS bundle.
   * ========================================================================= */
  function removeRoleNoneFromFooter(root) {
    var fixed = 0;
    // Any element with role="none" that contains or IS adjacent to a footer
    $$(
      '[role="none"]:has(footer), [role="none"] + footer, footer[role="none"]',
      root
    ).forEach(function (el) {
      el.removeAttribute('role');
      fixed++;
      log.ok('Fix 1: Removed role="none" from <' + el.tagName.toLowerCase()
        + '> id="' + el.id + '"');
    });

    // Broader fallback — any element with role="none" whose class suggests footer
    $$('[role="none"]', root).forEach(function (el) {
      var cls = el.className || '';
      var id  = el.id || '';
      if (/footer/i.test(cls) || /footer/i.test(id) || el.querySelector('footer')) {
        el.removeAttribute('role');
        fixed++;
        log.ok('Fix 1 (fallback): Removed role="none" from <'
          + el.tagName.toLowerCase() + '> id="' + el.id + '"');
      }
    });

    return fixed;
  }

  function fixFooterLandmark() {
    // Attempt 1 — run immediately
    var found = removeRoleNoneFromFooter(document);

    if (found === 0) {
      log.info('Fix 1: No role="none" footer wrapper found at DOM ready — '
        + 'watching for dynamic injection...');
    }

    // Attempt 2 & 3 — retry after delays (catches slow section renders)
    setTimeout(function () {
      var n = removeRoleNoneFromFooter(document);
      if (n > 0) log.ok('Fix 1: Caught dynamically rendered footer at 1s delay.');
    }, 1000);

    setTimeout(function () {
      var n = removeRoleNoneFromFooter(document);
      if (n > 0) log.ok('Fix 1: Caught dynamically rendered footer at 3s delay.');
    }, 3000);

    // Attempt 4 — MutationObserver watches for any new nodes added to <body>
    if (window.MutationObserver) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return; // elements only
            // Check the node itself and its descendants
            if (node.getAttribute && node.getAttribute('role') === 'none'
                && (node.querySelector('footer') || /footer/i.test(node.className + node.id))) {
              node.removeAttribute('role');
              log.ok('Fix 1 (MutationObserver): Patched dynamically added footer wrapper.');
            }
            // Also scan children of the added node
            removeRoleNoneFromFooter(node);
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Disconnect after 10s — the footer will have rendered by then
      setTimeout(function () { observer.disconnect(); }, 10000);
    }
  }

  /* =========================================================================
   * FIX 2 — Pagination <nav>: redundant role="navigation"
   * ========================================================================= */
  function fixPaginationNav() {
    var fixed = 0;
    $$('nav[role="navigation"]').forEach(function (el) {
      var label = (el.getAttribute('aria-label') || '').toLowerCase();
      var cls   = el.className.toLowerCase();
      if (label.indexOf('pagination') > -1 || cls.indexOf('pagination') > -1) {
        el.removeAttribute('role');
        if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', 'Pagination');
        fixed++;
      }
    });
    if (fixed > 0) log.ok('Fix 2: Removed redundant role="navigation" from ' + fixed + ' pagination nav(s).');
    else log.info('Fix 2: No redundant pagination nav role found.');
  }

  /* =========================================================================
   * FIX 3 — Mobile nav: redundant role="navigation"
   * ========================================================================= */
  function fixMobileNavRole() {
    var fixed = 0;
    $$('nav[role="navigation"]').forEach(function (el) {
      if (/mobile/i.test(el.getAttribute('aria-label') || '')) {
        el.removeAttribute('role');
        fixed++;
      }
    });
    if (fixed > 0) log.ok('Fix 3: Removed redundant role from ' + fixed + ' mobile nav(s).');
    else log.info('Fix 3: No redundant mobile nav role found.');
  }

  /* =========================================================================
   * FIX 4 — Header inline nav: ensure role + descriptive label
   * ========================================================================= */
  function fixMainNavLandmark() {
    var fixed = 0;
    $$('nav.header__inline-menu').forEach(function (el) {
      el.setAttribute('role', 'navigation');
      if (!el.getAttribute('aria-label') || el.getAttribute('aria-label') === 'Main') {
        el.setAttribute('aria-label', 'Main navigation');
      }
      fixed++;
    });
    if (fixed > 0) log.ok('Fix 4: Updated ' + fixed + ' main nav element(s).');
    else log.warn('Fix 4: .header__inline-menu not found.');
  }

  /* =========================================================================
   * FIX 5 — Sort dropdown: announce context change to screen readers
   *
   * ROOT CAUSE (confirmed by v2 console output):
   *   #SortBy wasn't found because on this Shopify theme the sort select
   *   is either:
   *     a) Inside a Shopify section that renders after DOMContentLoaded
   *     b) Using a different ID than expected
   *
   * FIX:
   *   1. Broader selector set — catches all known Dawn sort select variants.
   *   2. Retry with MutationObserver so late-rendered selects are caught.
   *   3. Use event delegation on document as ultimate fallback.
   * ========================================================================= */
  function fixSortAnnouncement() {
    // Ensure live region exists
    var liveRegion = document.getElementById('a11y-refresh-page-message');
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.id = 'a11y-refresh-page-message';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;'
        + 'margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
      document.body.appendChild(liveRegion);
      log.ok('Fix 5: Created aria-live region.');
    }

    function announce(msg) {
      liveRegion.textContent = '';
      setTimeout(function () { liveRegion.textContent = msg; }, 50);
    }

    var SORT_SELECTOR = [
      '#SortBy',
      '#SortBy-mobile',
      'select[name="sort_by"]',
      'select.facet-filters__sort',
      '[data-sort-by]',
      '.facets__form select'
    ].join(', ');

    function attachToSelects() {
      var found = 0;
      $$(SORT_SELECTOR).forEach(function (sel) {
        // Avoid attaching twice
        if (sel.dataset.a11ySort) return;
        sel.dataset.a11ySort = '1';
        sel.addEventListener('change', function () {
          announce('Updating product results, please wait.');
        });
        found++;
      });
      if (found > 0) log.ok('Fix 5: Attached sort listeners to ' + found + ' select(s).');
      return found;
    }

    // Attempt immediately
    var found = attachToSelects();

    // Retry at 1s and 3s for late-rendered selects
    if (found === 0) {
      log.info('Fix 5: Sort selects not found yet — retrying after delays...');
      setTimeout(function () {
        var n = attachToSelects();
        if (n === 0) log.warn('Fix 5: Still no sort selects at 1s. Will retry at 3s.');
      }, 1000);
      setTimeout(function () {
        var n = attachToSelects();
        if (n === 0) log.warn('Fix 5: No sort selects found at 3s either. '
          + 'Check DevTools Elements panel for the sort dropdown\'s actual id/class and reply with it.');
      }, 3000);
    }

    // Event delegation fallback — catches any select[name=sort_by] change
    // regardless of when it was added to the DOM
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (el.tagName === 'SELECT' && (
        el.name === 'sort_by' ||
        el.id === 'SortBy' ||
        el.id === 'SortBy-mobile' ||
        el.classList.contains('facet-filters__sort')
      )) {
        announce('Updating product results, please wait.');
      }
    });
    log.ok('Fix 5: Event delegation fallback active for all sort selects.');

    // Watch grid for completion
    var grid = document.getElementById('product-grid');
    if (grid && window.MutationObserver) {
      new MutationObserver(function (mutations) {
        if (mutations.some(function (m) { return m.addedNodes.length > 0; })) {
          announce('Product results updated.');
        }
      }).observe(grid, { childList: true });
      log.ok('Fix 5: Watching #product-grid for updates.');
    } else if (!grid) {
      log.warn('Fix 5: #product-grid not found — completion announcement unavailable.');
    }
  }

  /* =========================================================================
   * FIX 7 — Drawer aria-hidden + inert management
   * ========================================================================= */
  function fixDrawerAccessibility() {
    var managed = 0;

    function hide(el) { el.setAttribute('aria-hidden', 'true');  el.setAttribute('inert', ''); }
    function show(el) { el.setAttribute('aria-hidden', 'false'); el.removeAttribute('inert'); }

    $$('details').forEach(function (details) {
      if (!/menu/i.test(details.id || '')) return;
      var panel = details.querySelector('#menu-drawer, .menu-drawer, [id*="menu-drawer"]');
      if (!panel) return;

      details.open ? show(panel) : hide(panel);

      if (window.MutationObserver) {
        new MutationObserver(function () {
          details.open ? show(panel) : hide(panel);
        }).observe(details, { attributes: true, attributeFilter: ['open'] });
      }

      var summary = details.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', function () {
          details.open ? hide(panel) : show(panel);
        });
      }
      managed++;
    });

    $$('cart-drawer').forEach(function (cartEl) {
      var dialog = cartEl.querySelector('[role="dialog"]');
      if (!dialog) return;
      var isOpen = cartEl.classList.contains('active') || cartEl.classList.contains('is-open');
      isOpen ? show(dialog) : hide(dialog);
      if (window.MutationObserver) {
        new MutationObserver(function () {
          var open = cartEl.classList.contains('active') || cartEl.classList.contains('is-open');
          open ? show(dialog) : hide(dialog);
        }).observe(cartEl, { attributes: true, attributeFilter: ['class'] });
      }
      managed++;
    });

    if (managed > 0) log.ok('Fix 7: Managing ' + managed + ' drawer(s).');
    else log.warn('Fix 7: No drawers found.');
  }

  /* =========================================================================
   * FIX 8 — Non-button interactive elements
   * ========================================================================= */
  function fixMissingButtonRoles() {
    var fixed = 0;
    $$('[tabindex="0"]:not(button):not(a):not(input):not(select):not(textarea):not([role])').forEach(function (el) {
      var tag = el.tagName.toLowerCase();
      if (tag !== 'div' && tag !== 'span' && tag !== 'li') return;
      el.setAttribute('role', 'button');
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.click(); }
      });
      fixed++;
    });
    $$('header summary:not([role]), .header summary:not([role])').forEach(function (s) {
      s.setAttribute('role', 'button'); fixed++;
    });
    if (fixed > 0) log.ok('Fix 8: Patched ' + fixed + ' element(s).');
    else log.info('Fix 8: No missing button roles found.');
  }

  /* =========================================================================
   * FIX 9 — Review count spans: remove aria-hidden from visible (N) text
   * ========================================================================= */
  function fixReviewCountVisibility() {
    var fixed = 0;
    $$('span[aria-hidden="true"]').forEach(function (span) {
      var text = span.textContent.trim();
      if (!/^\(\d+\)$/.test(text)) return;
      var count = text.replace(/[()]/g, '').trim();
      var num = parseInt(count, 10);

      span.textContent = '';
      span.removeAttribute('aria-hidden');

      var visual = document.createElement('span');
      visual.setAttribute('aria-hidden', 'true');
      visual.textContent = '(' + count + ')';

      var srOnly = document.createElement('span');
      srOnly.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;'
        + 'margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
      srOnly.textContent = num === 1 ? '1 review' : count + ' reviews';

      span.appendChild(visual);
      span.appendChild(srOnly);
      fixed++;
    });
    if (fixed > 0) log.ok('Fix 9: Exposed ' + fixed + ' review count(s) to screen readers.');
    else log.info('Fix 9: No hidden (N) review spans found.');
  }

  /* =========================================================================
   * BONUS — Skip link
   * ========================================================================= */
  function fixSkipLink() {
    var link = document.querySelector('a[href="#MainContent"]');
    var main = document.getElementById('MainContent');
    if (!link || !main) return;
    if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
    link.addEventListener('click', function (e) { e.preventDefault(); main.focus(); });
    log.ok('Bonus (skip link): Wired up.');
  }

  /* =========================================================================
   * RUNNER
   * ========================================================================= */
  ready(function () {
    log.info('silkcity-a11y-fixes.js v3 loaded.');
    [
      ['Fix 1 — footer landmark',   fixFooterLandmark],
      ['Fix 2 — pagination nav',    fixPaginationNav],
      ['Fix 3 — mobile nav role',   fixMobileNavRole],
      ['Fix 4 — main nav landmark', fixMainNavLandmark],
      ['Fix 5 — sort announcement', fixSortAnnouncement],
      ['Fix 7 — drawer a11y',       fixDrawerAccessibility],
      ['Fix 8 — button roles',      fixMissingButtonRoles],
      ['Fix 9 — review counts',     fixReviewCountVisibility],
      ['Bonus — skip link',         fixSkipLink]
    ].forEach(function (pair) {
      try { pair[1](); }
      catch (e) { log.err(pair[0] + ' threw: ' + e.message); }
    });
    log.info('Initial run complete. Fix 1 and Fix 5 continue watching asynchronously.');
  });

})();